import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Apollo, gql } from "apollo-angular";
import {
  FormInputComponent,
  SharedModule,
  StringCustomFieldConfig,
} from "@vendure/admin-ui/core";
import { Subject } from "rxjs";
import {
  takeUntil,
  filter,
  distinctUntilChanged,
  debounceTime,
} from "rxjs/operators";
import { CountryStateService } from "./country-state.service";

@Component({
  imports: [SharedModule],
  standalone: true,
  selector: "app-state-selector",
  template: `
    <div style="position:relative">
      <ng-select
        [items]="allStates"
        [multiple]="true"
        bindLabel="state"
        [placeholder]="'common.select' | translate"
        [formControl]="formControl"
        class="form-control"
        style="text-transform: capitalize"
        [hideSelected]="true"
        [dropdownPosition]="'top'"
        [closeOnSelect]="false"
      >
      </ng-select>
    </div>
  `,
})
export class StateSelectorComponent
  implements OnInit, OnDestroy, FormInputComponent<StringCustomFieldConfig>
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;
  allStates: string[] = [];
  private destroy$ = new Subject<void>();

  private query = gql`
    query GetCountryDetail($id: ID!) {
      country(id: $id) {
        id
        name
        customFields {
          states
        }
      }
    }
  `;

  constructor(
    private apollo: Apollo,
    private countryStateService: CountryStateService,
  ) {
    console.log(
      "🎯 StateSelectorComponent Constructor called at",
      new Date().toISOString(),
    );
  }

  ngOnInit() {
    // Add debounce and filter to handle timing issues
    this.countryStateService.countryId$
      .pipe(
        takeUntil(this.destroy$),
        filter((countryId) => !!countryId), // Only proceed if countryId exists
        distinctUntilChanged(), // Prevent duplicate calls
        debounceTime(50), // Small delay to ensure everything is ready
      )
      .subscribe((countryId) => {
        console.log("from listener", countryId);
        if (!countryId) {
          return;
        }
        this.fetchStates(countryId);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fetchStates(countryId: string) {
    this.apollo
      .query({
        query: this.query,
        variables: { id: countryId },
        fetchPolicy: "network-only", // Always fetch fresh data
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          const statesString = result.data?.country?.customFields?.states;

          if (statesString) {
            this.allStates = statesString
              .split(",")
              .map((state: string) => state.trim().toLowerCase())
              .filter((state: string) => state.length > 0);
          } else {
            this.allStates = [];
          }

          // Validate current selection
          this.validateCurrentSelection();
        },
        error: (error) => {
          console.error("Error fetching states", error);
          this.allStates = [];
        },
      });
  }

  private validateCurrentSelection() {
    const currentValue = this.formControl.value;
    if (currentValue && Array.isArray(currentValue)) {
      const validValues = currentValue.filter((val: string) =>
        this.allStates.includes(val),
      );
      if (validValues.length !== currentValue.length) {
        this.formControl.setValue(validValues);
      }
    }
  }
}
